import React, { useEffect, useState } from 'react';
import { ItemToReturn } from '@src/models/returns';
import { RETURN_METHOD, ReturnMethodType } from '@src/constants/general';
import { setUpdatedItem } from '@src/stores/returnStore';
import { getFeatureSwitchStatic } from '@src/utils/general';
import CardMethod from './CardMethod';
import CardMethodDescription from './CardMethodDescription';
import ItemImageWithQuantityBadge from '../shared/items/ItemWithQuantityBadge';
import ItemReturnMethodError from './errors/ItemReturnMethodError';

interface ItemReturnMethodProps {
  item: ItemToReturn;
  showError?: boolean;
}

const headerMap: { [key in ReturnMethodType]: string } = {
  'In-Store': 'In-Store Return',
  UPS: 'UPS Drop-Off',
  LORP: 'Return Pickup',
  SORIS: 'In-Store Return',
};

const ItemReturnMethod = ({
  item,
  showError = false,
}: ItemReturnMethodProps) => {
  // TODO: when SORIS is online, we need to default to In-Store at the start
  const [selectedCard, setSelectedCard] = useState<ReturnMethodType | ''>(
    item.returnMethod || '',
  );

  // If SORIS is currently blocked, we have to make sure basic In-Store return is always allowed for the item.
  const allowSORIS = getFeatureSwitchStatic('allowSORIS');

  const itemMethodOptions = (
    Object.values(RETURN_METHOD) as Array<ReturnMethodType>
  ).filter((option) => {
    // Hide In-Store card if SORIS is available (show SORIS card instead which displays as "In-Store Return")
    if (option === 'In-Store' && allowSORIS && item.eligibilities?.SORIS) {
      return false;
    }
    return (
      item.eligibilities?.[option] === true ||
      (option === 'In-Store' && !allowSORIS) || // TODO : we need to take care of this when SORIS is live
      (option === 'SORIS' && allowSORIS && item.eligibilities?.SORIS)
    );
  }) as ReturnMethodType[];

  const handleMethodClick = (method: ReturnMethodType) => {
    setSelectedCard(method);
  };

  useEffect(() => {
    if (selectedCard === '') return;
    const updatedItem = { ...item, returnMethod: selectedCard };
    setUpdatedItem(updatedItem);
  }, [selectedCard]);

  return (
    <div data-testid="item-return-method">
      <div key={item?.lineItemId} className="sui-relative sui-mb-4">
        <ItemImageWithQuantityBadge
          itemDescription={item?.itemDescription}
          imageUrl={item?.imageUrl}
          returnQuantity={item?.returnQuantity}
        />
      </div>

      {showError && !selectedCard && <ItemReturnMethodError />}

      <div className="sui-flex sui-flex-col sui-gap-4">
        {itemMethodOptions.map((option) => {
          return (
            <CardMethod
              key={option}
              header={headerMap[option]}
              onClick={() => handleMethodClick(option)}
              selected={selectedCard === option}
            >
              <CardMethodDescription method={option} />
            </CardMethod>
          );
        })}
      </div>
    </div>
  );
};

export default ItemReturnMethod;
